import { useEffect, useState, useRef, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Box,
  Chip,
  Grid,
  Button,
  TextField,
  MenuItem,
  Stack,
  Paper,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDButton from "components/MDButton";
import LoadingScreen from "components/Loading";
import {
  addCompetency,
  getCompetencies,
  deleteCompetencyById,
  updateCompetency,
  getCompetencyLevels,
  addCompetencyLevel,
  updateCompetencyLevel,
  endCompetencyLevel,
  exportCompetencies,
  exportCompetencyLevels,
} from "services/api";
import { useAuth } from "context/authContext";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import AddCompetencyDialog from "components/AddCompetencyDialog";
import { Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert } from "@mui/material";
import ConfirmationDialog from "components/ConfirmationDialog";
import AddCompetencyLevelDialog from "components/AddCompLevelDialog";
import CircularProgress from "@mui/material/CircularProgress";
import AddFloatingButton from "components/AddFloatingButton";
import SnackbarWrapper from "components/Snackbar";
import { Addchart, Download } from "@mui/icons-material";
import { CloudDownload } from "@mui/icons-material";
import { Menu } from "@mui/material";
import Fab from "@mui/material/Fab";
import Icon from "@mui/material/Icon";
import { useTranslation } from "react-i18next";
import { buttonStyles } from "services/utils";
import ImportButton from "components/ImportButton";
import { linkCompetencyToKpis } from "services/api";
import { getKpiNames } from "services/api";
import KpiMappingDialog from "components/KpiMappingDialog";

const fieldStyles = {
  height: 50, // Adjust overall height
  "& .MuiInputBase-root": {
    height: 50, // Input field height
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-select": {
    padding: 12, // Adjust padding inside select
  },
};

const CompetencyToLevelTab = ({ canEdit }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { employee } = useAuth();
  const snackbarRef = useRef();
  const [competency, setCompetencies] = useState([]);
  const [levelNames, setLevelNames] = useState([]);
  const [selectedCompetency, setSelectedCompetency] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editedCompetency, setEditedCompetency] = useState(null);
  const [confOpen, setConfOpen] = useState(false);
  const [confOpen2, setConfOpen2] = useState(false);
  const [openLevelDialog, setOpenLevelDialog] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedLevel2, setSelectedLevel2] = useState("");
  const [levels, setLevels] = useState([]);
  const [selectedCompetencyId, setSelectedCompetencyId] = useState(null);
  //Snackbar
  const [isUpdating, setIsUpdating] = useState(false);
  const [levelEditStates, setLevelEditStates] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [exportAnchor, setExportAnchor] = useState(null);
  const openExportMenu = (e) => setExportAnchor(e.currentTarget);
  const closeExportMenu = () => setExportAnchor(null);
  const [importAnchor, setImportAnchor] = useState(null);

  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [targetCompetency, setTargetCompetency] = useState(null);
  const [allKpis, setAllKpis] = useState([]);

  const handleAddComp = async (newComp) => {
    Object.keys(newComp).forEach((key) => {});
    try {
      const addedComp = await addCompetency(newComp); // ✅ Call API
      setCompetencies((prev) => [...prev, addedComp]); // ✅ Update UI
      setOpenAddDialog(false);
    } catch (error) {}
  };

  const handleSaveKpiMappings = async (competencyId, mappings) => {
    try {
      // This matches the DTO we created earlier
      const payload = {
        competencyId: competencyId,
        kpis: mappings,
      };
      await linkCompetencyToKpis(payload);
      setKpiDialogOpen(false);
      // Add success notification or refresh data
    } catch (error) {
      console.error("Failed to sync KPIs", error);
    }
  };

  const handleSaveLevel = async (newLevel) => {
    try {
      const addedLevel = await addCompetencyLevel(newLevel); // ✅ Call API
      setLevels((prev) => [...prev, addedLevel]); // ✅ Update UI
    } catch (error) {}
  };

  const handleLevelChange = (id, newValue) => {
    setLevels((prevLevels) =>
      prevLevels.map((lvl) =>
        lvl.competencyLevelId === id ? { ...lvl, definition: newValue } : lvl
      )
    );
    setLevelEditStates((prev) => {
      const updatedState = { ...prev, [id]: true }; // Marks the field as edited
      return updatedState;
    });
  };

  const handleLevelBlur = async (updatedLevel) => {
    try {
      const savedLevel = await updateCompetencyLevel(updatedLevel); // Call your API

      if (savedLevel !== "error") {
        setLevels((prevLevels) => {
          const updatedLevels = prevLevels.map((lvl) =>
            lvl.competencyLevelId === savedLevel.competencyLevelId ? savedLevel : lvl
          );
          // 🟢 Always sort by numeric value after update
          return [...updatedLevels].sort((a, b) => a.value - b.value);
        });
      }
      const id = updatedLevel.competencyLevelId;
      setLevelEditStates((prev) => ({ ...prev, [id]: false }));
      // setLevelSaveStates((prev) => ({ ...prev, [id]: true }));
      snackbarRef.current?.showSnackbar(t("admin.competency.editDialog.updateSuccess"), "success");
    } catch (error) {
      snackbarRef.current?.showSnackbar(t("admin.competency.editDialog.updateFailed"), "error");
    }
  };

  const handleSaveChanges = async () => {
    if (!editedCompetency) return;

    if (!editedCompetency.name) {
      snackbarRef.current?.showSnackbar(t("admin.competency.validation.nameRequired"), "error");
      return;
    }

    if (!editedCompetency.type) {
      snackbarRef.current?.showSnackbar(t("admin.competency.validation.typeRequired"), "error");
      return;
    }

    // Check if the name already exists (case-insensitive), excluding the current competency
    const nameExists = competency.some(
      (comp) =>
        comp.name.toLowerCase() === editedCompetency.name.toLowerCase() &&
        comp.competencyId !== editedCompetency.competencyId
    );
    if (nameExists) {
      snackbarRef.current?.showSnackbar(t("admin.competency.validation.nameExists"), "error");
      return;
    }

    try {
      setIsUpdating(true);
      const updatedCompetency = await updateCompetency(editedCompetency);

      if (updatedCompetency !== "error") {
        setCompetencies((prevCompetencies) =>
          prevCompetencies.map((comp) =>
            comp.competencyId === updatedCompetency.competencyId ? updatedCompetency : comp
          )
        );
        setOpenEditDialog(false); // Close the dialog after successful update
      }
    } catch (error) {
    } finally {
      setIsUpdating(false); // 🔴 Stop loading
    }
  };

  const handleCloseLevelDialog = () => {
    setOpenLevelDialog(false);
    setSelectedCompetency(null);
    setSelectedLevel(""); // Reset selection
  };

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  const handleOpenAddDialog = () => {
    setSelectedCompetency(null);
    // setOpenEditDialog(false); // Ensure edit
    setOpenAddDialog(true); // UPDATE: Only opens AddInitiativeDialog
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedCompetency(null);
  };

  const handleOpenEditDialog = (comp) => {
    if (!canEdit) return;
    setEditedCompetency({ ...comp }); // Copy the initiative data
    setOpenEditDialog(true);
    setSelectedCompetency(comp);
  };

  const handleOpenLevelDialog = (comp) => {
    setOpenLevelDialog(true);
    setSelectedCompetency(comp);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedCompetency(null);
  };

  const handleDeleteComp = async () => {
    if (!selectedCompetency) return;
    setConfOpen(true);
  };

  const handleOpenKpiDialog = (comp) => {
    setTargetCompetency(comp);
    setKpiDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedCompetency((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!loading) setLoading(true);

      const compData = await getCompetencies();
      setCompetencies(compData);

      const kpiNames = await getKpiNames();
      setAllKpis(kpiNames);

      const competencyLevels = await getCompetencyLevels();
      const sortedLevels = [...competencyLevels].sort((a, b) => a.value - b.value);
      setLevels(sortedLevels);

      // const levelNames = competencyLevels?.map((item) => item.levelName);
      const levelNames = sortedLevels?.map((item) => item.levelName);
      setLevelNames(levelNames);
    };

    fetchData().then((e) => {
      setLoading(false);
    });
  }, [location]);

  const groupedCompetencies = useMemo(() => {
    const lower = searchTerm.toLowerCase();

    const filtered = !searchTerm
      ? competency
      : competency?.filter(
          (comp) =>
            comp.name.toLowerCase().includes(lower) ||
            (comp.description && comp.description.toLowerCase().includes(lower))
        );

    return Object.entries(
      filtered?.reduce((acc, comp) => {
        const category = comp.type || t("admin.competency.uncategorized");
        if (!acc[category]) acc[category] = [];
        acc[category].push(comp);
        return acc;
      }, {})
    );
  }, [competency, searchTerm, t]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Grid container spacing={3} mb={4} ml={0} alignItems="stretch">
        <Grid container spacing={2}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <TextField
              label={t("admin.competency.searchComp")}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 300 }}
            />

            {/* Replaced with Menu-based Export */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box>
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={openExportMenu}
                  disabled={exportLoading}
                  startIcon={<CloudDownload />}
                >
                  {!exportLoading ? (
                    t("common.export")
                  ) : (
                    <CircularProgress size={20} color="inherit" />
                  )}
                </MDButton>

                <Menu
                  anchorEl={exportAnchor}
                  open={Boolean(exportAnchor)}
                  onClose={closeExportMenu}
                >
                  <MenuItem
                    onClick={async () => {
                      closeExportMenu();
                      setExportLoading(true);
                      await exportCompetencies();
                      setExportLoading(false);
                    }}
                  >
                    {t("Export Competencies")}
                  </MenuItem>

                  <MenuItem
                    onClick={async () => {
                      closeExportMenu();
                      setExportLoading(true);
                      await exportCompetencyLevels();
                      setExportLoading(false);
                    }}
                  >
                    {t("Export Competency Levels")}
                  </MenuItem>
                </Menu>
              </Box>
              <ImportButton
                allowedTypes={["competencies", "competencyLevels"]}
                onError={handleImportError}
                disabled={!canEdit}
              />
            </Box>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" p={1}>
              <CircularProgress size={20} />
            </Box>
          ) : competency.length === 0 ? (
            <Typography variant="h6" textAlign="center" width="100%">
              {t("admin.competency.no_competencies_found")}
            </Typography>
          ) : (
            groupedCompetencies.map(([category, comps]) => (
              <Box
                key={category}
                sx={{
                  width: "100%",
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="primary"
                    sx={{
                      mb: 1,
                      mt: 2,
                      textTransform: "capitalize",
                      textAlign: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {category}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {comps?.map((comp) => {
                    const isSelected = selectedCompetencyId === comp.competencyId;
                    const compLevels = levels?.filter(
                      (lvl) => lvl.competencyId === comp.competencyId && lvl.endDate == null
                    );

                    return (
                      <Grid
                        item
                        key={comp.competencyId}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        sx={{ mt: 2 }}
                      >
                        <Paper
                          elevation={3}
                          sx={{
                            p: 2,
                            mt: 1,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                            border: isSelected ? "2px solid #4caf50" : "1px solid #ccc",
                            borderRadius: "12px",
                            cursor: "pointer",
                            transition: "border-color 0.3s",
                            "&:hover": {
                              borderColor: "#4caf50",
                            },
                          }}
                          onClick={() => handleOpenEditDialog(comp)}
                        >
                          <Tooltip title={t("admin.competency.manage_kpis")} placement="top" arrow>
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                color: "#00717D",
                                backgroundColor: "rgba(0, 113, 125, 0.05)",
                                "&:hover": { backgroundColor: "rgba(0, 113, 125, 0.15)" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent opening the edit dialog
                                handleOpenKpiDialog(comp); // Define this function in your parent component
                              }}
                            >
                              <Addchart fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={comp.name} placement="top" arrow>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{
                                px: 1,
                                color: "#00717D",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                lineHeight: "1.2em",
                                maxHeight: "2.4em",
                                minHeight: "2.4em",
                                wordBreak: "break-word",
                                textAlign: "center",
                                mx: "auto",
                              }}
                            >
                              {comp.name}
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            fontWeight="bold"
                            sx={{
                              textAlign: "center",
                              mt: 1,
                              color: "#444444",
                            }}
                          >
                            {t("admin.competency.definition")}
                          </Typography>

                          <Box
                            sx={{
                              flex: 1,
                              px: 1,
                              mt: 1,
                              backgroundColor: "#f9f9f9",
                              borderRadius: 1,
                              fontSize: "0.875rem",
                              lineHeight: "1.5",
                              textAlign: "left",
                              wordBreak: "break-word",
                            }}
                          >
                            {/* Clamped Content */}
                            <Box
                              sx={{
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                whiteSpace: "initial",
                              }}
                            >
                              <div dangerouslySetInnerHTML={{ __html: comp.definition }} />
                            </Box>
                          </Box>

                          <Box sx={{ mt: 2 }}>
                            <Accordion
                              sx={{
                                mt: 2,
                                mx: { xs: 0, sm: 1 }, // Responsive margins
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  textAlign: "left",
                                  border: "1px solid #ddd",
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  minHeight: "48px",
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Typography fontWeight="bold" noWrap>
                                  {t("admin.competency.levels")}
                                </Typography>
                              </AccordionSummary>

                              <AccordionDetails
                                sx={{
                                  height: 200,
                                  overflowY: "auto",
                                  px: 1,
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {compLevels?.map((level) => (
                                  <Accordion key={level.competencyLevelId} disableGutters>
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                      sx={{
                                        borderBottom: "1px solid #ddd",
                                        px: 1,
                                        py: 0.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          width: "100%",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                          sx={{
                                            flexGrow: 1,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <Box
                                            component="span"
                                            sx={{ color: "#444444", fontWeight: "bold", mr: 0.5 }}
                                          >
                                            {level.levelName}
                                          </Box>
                                          <Box component="span">: {level.value}</Box>
                                        </Typography>

                                        <IconButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedLevel2(level);
                                            setConfOpen2(true);
                                          }}
                                          disabled={!canEdit}
                                          sx={{
                                            padding: 0,
                                            marginLeft: 2,
                                            color: "#d32f2f",
                                            "&:hover": {
                                              backgroundColor: "#fddede",
                                            },
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 1 }}>
                                      <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={level.definition}
                                        disabled={!canEdit}
                                        onFocus={() =>
                                          setLevelEditStates((prev) => ({
                                            ...prev,
                                            [level.competencyLevelId]: true,
                                          }))
                                        }
                                        onChange={(e) =>
                                          handleLevelChange(level.competencyLevelId, e.target.value)
                                        }
                                        onBlur={() => handleLevelBlur(level)}
                                        variant="outlined"
                                        placeholder={t("admin.competency.enter_definition")}
                                        sx={{
                                          mt: 1,
                                          "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                              borderColor: levelEditStates[level.competencyLevelId]
                                                ? "green"
                                                : "transparent",
                                              transition: "border 0.3s ease-in-out",
                                            },
                                            "&:hover fieldset": { borderColor: "#ccc" },
                                            "&.Mui-focused fieldset": {
                                              borderColor: "#ccc",
                                              boxShadow: "none",
                                            },
                                          },
                                        }}
                                      />
                                    </AccordionDetails>
                                  </Accordion>
                                ))}
                              </AccordionDetails>
                            </Accordion>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mt: 2,
                              mb: 1,
                            }}
                          >
                            <MDButton
                              color="primary"
                              sx={{
                                ...buttonStyles(true),
                                width: { xs: "100%", sm: "auto" }, // Full width on mobile
                              }}
                              disabled={!canEdit}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLevel(comp);
                                setSelectedCompetencyId(comp.competencyId);
                                handleOpenLevelDialog(comp);
                              }}
                            >
                              {t("admin.competency.add_level")}
                            </MDButton>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))
          )}
        </Grid>
      </Grid>

      {canEdit && <AddFloatingButton onClick={handleOpenAddDialog} />}

      <ConfirmationDialog
        open={confOpen2}
        onClose={() => setConfOpen2(false)} // Close dialog without any action
        onConfirm={async () => {
          try {
            await endCompetencyLevel(selectedLevel2.competencyLevelId);
            setLevels((prevLevels) =>
              prevLevels.filter(
                (level) => level.competencyLevelId !== selectedLevel2.competencyLevelId
              )
            );

            setConfOpen2(false); // Close confirmation dialog
          } catch (error) {}
        }}
      />

      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} fullWidth>
        <DialogTitle>{t("admin.competency.editDialog.edit_competency")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("admin.competency.editDialog.competency_name")}
            name="name"
            fullWidth
            margin="normal"
            value={editedCompetency?.name || ""}
            onChange={handleInputChange}
          />

          <Typography variant="body2" sx={{ mt: 2 }}>
            {t("admin.competency.editDialog.description")}
          </Typography>
          <ReactQuill
            value={editedCompetency?.definition || ""}
            onChange={(value) => setEditedCompetency((prev) => ({ ...prev, definition: value }))}
            style={{ color: "black", fontSize: "16px", marginBottom: "16px" }}
          />

          <TextField
            label={t("admin.competency.editDialog.criteria")}
            name="type"
            fullWidth
            margin="normal"
            value={editedCompetency?.type || ""}
            onChange={handleInputChange}
            select // ✅ Enables dropdown
            sx={{
              width: "100%", // ✅ Custom width
              "& .MuiInputBase-root": { height: "30px" }, // ✅ Increase field height
              "& .MuiInputBase-input": { fontSize: "0.8rem", padding: "16px" }, // ✅ Bigger text inside
              "& .MuiInputLabel-root": { fontSize: "1.0rem" }, // ✅ Bigger label
              ...fieldStyles,
            }}
          >
            <MenuItem value="Leadership">Leadership</MenuItem>
            <MenuItem value="Technical">Technical</MenuItem>
            <MenuItem value="Core">Core</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          {isUpdating ? (
            <Box display="flex" justifyContent="center" p={1}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Button onClick={() => setOpenEditDialog(false)} color="secondary">
                {t("admin.competency.editDialog.cancel")}
              </Button>
              {
                <Button
                  onClick={handleDeleteComp}
                  color="error"
                  sx={{
                    color: "red",
                    "&:hover": { backgroundColor: "red", color: "#fff" },
                  }}
                >
                  {t("admin.competency.editDialog.delete")}
                </Button>
              }
              {
                <Button
                  onClick={handleSaveChanges}
                  color="primary"
                  sx={{
                    "&:hover": { backgroundColor: "blue", color: "#fff" },
                  }}
                >
                  {t("admin.competency.editDialog.update")}
                </Button>
              }
            </>
          )}
        </DialogActions>
      </Dialog>

      <AddCompetencyDialog
        open={openAddDialog} // UPDATE: Uses separate state
        onClose={handleCloseAddDialog} // UPDATE: Uses separate close function
        competency={selectedCompetency}
        handleDelete={handleDeleteComp}
        handleSubmit={handleAddComp}
        companyId={employee.companyId}
        competencies={competency}
      />
      {
        <ConfirmationDialog
          open={confOpen}
          onClose={() => {
            setConfOpen(false);
          }}
          onConfirm={async () => {
            try {
              await deleteCompetencyById(selectedCompetency.competencyId);

              // Remove the deleted initiative from the UI
              setCompetencies((prev) =>
                prev.filter((comp) => comp.competencyId !== selectedCompetency.competencyId)
              );
              setOpenEditDialog(false); // Close Edit dialog
            } catch (error) {
              // alert(error.message);
              snackbarRef.current?.showSnackbar(error.message, "error");
            }
            setConfOpen(false);
          }}
        />
      }

      {
        <AddCompetencyLevelDialog
          open={openLevelDialog}
          onClose={handleCloseLevelDialog}
          handleSubmit={handleSaveLevel}
          level={selectedLevel}
          levelNames={levelNames}
          competencyId={selectedCompetencyId} // ✅ Now available inside dialog
        />
      }

      <KpiMappingDialog
        open={kpiDialogOpen}
        onClose={() => setKpiDialogOpen(false)}
        competency={targetCompetency}
        onSave={handleSaveKpiMappings}
        allKpis={allKpis}
      />
      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
    </Box>
  );
};

CompetencyToLevelTab.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default CompetencyToLevelTab;
